import { BUILT_IN_DEFAULT_TAGS } from "@storytell/ui/live-summary";
import { TbBuildingSkyscraper, TbChartBar, TbCurrencyDollar, TbRocket } from "solid-icons/tb";
import type {
  ActionsData,
  AgentHandoffData,
  AskData,
  FollowThroughData,
  InsightsData,
  LibraryData,
  LibraryLabel,
  ListenFlowData,
  MultiplayerData,
  OfferFlowData,
  Person,
  RoundTripData,
  SmartTagsData,
  SpeakersData,
} from "~/components/marketing/mockups";
import type { useI18n } from "~/i18n";

type Translate = ReturnType<typeof useI18n>["t"];

/**
 * Demo data for the marketing mockups: realistic sessions at Hooli and Acme. The
 * cast's names stay untranslated, like any proper noun in a transcript. In a
 * transcript the speaker on this machine is `local`, which the desktop app
 * draws in gold and everyone else in blue.
 */
const PEOPLE = {
  dana: { name: "Dana", color: "#8b5cf6" },
  raj: { name: "Raj", color: "#0d9488" },
  priya: { name: "Priya", color: "#d97706" },
  maya: { name: "Maya", color: "#2563eb" },
  tom: { name: "Tom", color: "#db2777" },
  dan: { name: "Dan", color: "#ea580c" },
} satisfies Record<string, Person>;

export const listenFlowDemo = (t: Translate): ListenFlowData => ({
  steps: [
    { title: t("marketing.home.how.join_title"), body: t("marketing.home.how.join_body") },
    { title: t("marketing.home.how.follow_title"), body: t("marketing.home.how.follow_body") },
    { title: t("marketing.home.how.offer_title"), body: t("marketing.home.how.offer_body") },
  ],
  clock: "00:01",
  people: [PEOPLE.maya, PEOPLE.dan],
  lines: [
    { speaker: PEOPLE.maya.name, local: true, time: "00:04", text: t("marketing.home.how.line1") },
    { speaker: PEOPLE.dan.name, time: "00:11", text: t("marketing.home.how.line2") },
  ],
  offer: t("marketing.home.how.offer"),
  filed: t("marketing.home.how.filed", { id: "LIN-482" }),
});

export const offerFlowDemo = (t: Translate): OfferFlowData => ({
  project: "Hooli launch",
  session: t("marketing.mock.offer.session"),
  started: "2026-10-08T14:00",
  clock: "24:16",
  people: [PEOPLE.dana, PEOPLE.raj, PEOPLE.priya],
  transcript: [
    { speaker: "Dana", local: true, time: "23:41", text: t("marketing.mock.offer.line1") },
    { speaker: "Raj", time: "23:52", text: t("marketing.mock.offer.line2") },
    { speaker: "Dana", local: true, time: "24:05", text: t("marketing.mock.offer.line3") },
    { speaker: "Priya", time: "24:11", text: t("marketing.mock.offer.line4") },
  ],
  offer: { why: t("marketing.mock.offer.why") },
  issue: {
    title: t("marketing.mock.offer.issue"),
    team: "Web",
    priority: t("marketing.mock.offer.label_high"),
    ref: "WEB-214",
  },
});

export const insightsDemo = (t: Translate): InsightsData => ({
  project: "Hooli launch",
  session: t("marketing.mock.insights.session"),
  started: "2026-10-08T15:00",
  clock: "18:31",
  people: [PEOPLE.dana, PEOPLE.raj, PEOPLE.priya],
  transcript: [
    { speaker: "Raj", time: "18:02", text: t("marketing.mock.insights.line1") },
    { speaker: "Dana", local: true, time: "18:09", text: t("marketing.mock.insights.line2") },
    { speaker: "Priya", time: "18:16", text: t("marketing.mock.insights.line3") },
  ],
  asked: t("marketing.mock.insights.asked"),
  lookingAnswer: t("marketing.mock.insights.looking_answer"),
  lookingFound: t("marketing.mock.insights.looking_found"),
  answer: {
    time: "18:02",
    title: t("marketing.mock.insights.answer_title"),
    body: t("marketing.mock.insights.answer_body"),
  },
  found: {
    time: "18:16",
    title: t("marketing.mock.insights.found_title"),
    body: t("marketing.mock.insights.found_body"),
  },
  earlier: { time: "11:40", title: t("marketing.mock.insights.earlier_title") },
});

export const askDemo = (t: Translate): AskData => ({
  project: "Acme",
  session: t("marketing.mock.ask.session"),
  started: "2026-10-08T11:00",
  clock: "31:02",
  people: [PEOPLE.maya, PEOPLE.tom],
  transcript: [
    { speaker: "Tom", time: "30:12", text: t("marketing.mock.ask.line1") },
    { speaker: "Maya", local: true, time: "30:40", text: t("marketing.mock.ask.line2") },
    { speaker: "Tom", time: "30:51", text: t("marketing.mock.ask.line3") },
  ],
  selected: 1,
  question: t("marketing.mock.ask.question"),
  answer: t("marketing.mock.ask.answer"),
  sources: [
    { kind: "session", label: t("marketing.mock.ask.source_call") },
    { kind: "notion", label: t("marketing.mock.ask.source_doc") },
  ],
});

export const smartTagsDemo = (t: Translate): SmartTagsData => {
  const [todo, decision] = BUILT_IN_DEFAULT_TAGS;
  const risks = {
    id: "demo:risks",
    name: t("marketing.mock.tags.risks"),
    color: "#ef4444",
    smart: true,
    instruction: t("marketing.mock.tags.risks_instruction"),
  };
  const pilot = t("marketing.mock.tags.topic_pilot");
  const hiring = t("marketing.mock.tags.topic_hiring");
  return {
    project: "Hooli launch",
    session: t("marketing.mock.tags.session"),
    started: "2026-10-08T11:00",
    clock: "31:08",
    people: [PEOPLE.dana, PEOPLE.raj, PEOPLE.priya],
    tags: [
      { ...todo, name: t("marketing.mock.tags.todos") },
      { ...decision, name: t("marketing.mock.tags.decisions") },
      risks,
    ],
    focusTagId: decision.id,
    notes: [
      { id: "n1", topic: pilot, text: t("marketing.mock.tags.note1"), tagId: decision.id, at: 0.6 },
      { id: "n2", topic: pilot, text: t("marketing.mock.tags.note2"), tagId: todo.id, at: 1.5 },
      { id: "n3", topic: pilot, text: t("marketing.mock.tags.note3"), tagId: decision.id, at: 2.4 },
      { id: "n4", topic: hiring, text: t("marketing.mock.tags.note4"), tagId: risks.id, at: 3.3 },
      { id: "n5", topic: hiring, text: t("marketing.mock.tags.note5"), tagId: decision.id, at: 4.2 },
      { id: "n6", topic: hiring, text: t("marketing.mock.tags.note6"), tagId: todo.id, at: 5.1 },
    ],
  };
};

export const speakersDemo = (t: Translate): SpeakersData => {
  const unnamed = t("marketing.mock.speakers.speaker", { n: "2" });
  const name = "Sarah";
  const topic = t("marketing.mock.speakers.topic");
  const [todo, decision] = BUILT_IN_DEFAULT_TAGS;
  const todos = { ...todo, name: t("marketing.mock.tags.todos") };
  const decisions = { ...decision, name: t("marketing.mock.tags.decisions") };
  return {
    project: "Hooli launch",
    session: t("marketing.mock.speakers.session"),
    started: "2026-10-08T14:00",
    clock: "14:31",
    people: [PEOPLE.dana, PEOPLE.raj],
    name,
    known: [PEOPLE.dana.name, PEOPLE.priya.name, PEOPLE.raj.name],
    transcript: [
      { speaker: PEOPLE.dana.name, local: true, time: "14:02", text: t("marketing.mock.speakers.line1") },
      { speaker: unnamed, unnamed: true, time: "14:09", text: t("marketing.mock.speakers.line2") },
      { speaker: PEOPLE.dana.name, local: true, time: "14:15", text: t("marketing.mock.speakers.line3") },
      { speaker: unnamed, unnamed: true, time: "14:21", text: t("marketing.mock.speakers.line4") },
    ],
    next: { speaker: name, time: "14:28", text: t("marketing.mock.speakers.line5") },
    topic,
    notes: [{ text: t("marketing.mock.speakers.note1"), tag: decisions }, { text: t("marketing.mock.speakers.note2") }],
    todo: { text: t("marketing.mock.speakers.todo", { name }), tag: todos, owner: name },
  };
};

const PROJECT_FOLDER = "/Users/dev/code/billing-api";

export const agentHandoffDemo = (t: Translate): AgentHandoffData => ({
  project: "Platform",
  session: t("marketing.mock.handoff.session"),
  duration: "38m 40s",
  folder: PROJECT_FOLDER,
  tag: { id: "demo:billing", name: "billing", color: "#14b8a6" },
  delivered: `${PROJECT_FOLDER}/2026-10-02-billing-api-planning-g1`,
  loaded: t("marketing.mock.handoff.loaded"),
  prompt: t("marketing.mock.handoff.prompt", { path: `${PROJECT_FOLDER}/2026-10-02-billing-api-planning-g1` }),
  agents: [
    {
      name: "Claude Code",
      logo: { src: "/images/logos/claude.svg", color: "#D97757" },
      launch: { kind: "terminal", command: "claude" },
      plan: t("marketing.mock.handoff.plan"),
    },
    {
      name: "Codex",
      logo: { src: "/images/logos/openai.svg", color: "#000000", dark: true },
      launch: { kind: "terminal", command: "codex" },
      plan: t("marketing.mock.handoff.plan_codex"),
    },
    {
      name: "Cursor",
      logo: { src: "/images/logos/cursor.svg", color: "#000000", dark: true },
      launch: { kind: "paste" },
      plan: t("marketing.mock.handoff.plan_cursor"),
    },
  ],
});

// Collaboration colours are the desktop app's participant palette, in join order.
const TEAM = {
  alex: { name: "Alex", color: "#3b82f6" },
  sam: { name: "Sam", color: "#10b981" },
  priya: { name: "Priya", color: "#f59e0b" },
  dana: { name: "Dana", color: "#ec4899" },
} satisfies Record<string, Person>;

export const multiplayerDemo = (t: Translate): MultiplayerData => ({
  project: "Hooli launch",
  session: t("marketing.mock.multi.session"),
  started: "2026-10-08T09:30",
  clock: "12:41",
  host: TEAM.alex,
  others: [TEAM.priya, TEAM.dana],
  joiner: TEAM.sam,
  sketcher: TEAM.priya,
  more: 2,
  note: t("marketing.mock.collab.note"),
  doc: {
    title: t("marketing.mock.collab.doc_title"),
    body: t("marketing.mock.collab.doc_body"),
    typing: [
      { text: t("marketing.mock.collab.doc_typed"), typist: TEAM.alex },
      { text: t("marketing.mock.collab.doc_typed_rollout"), typist: TEAM.sam },
    ],
  },
  link: "chief.bot/s/launch-review",
  shared: {
    transcript: [
      { speaker: "Alex", local: true, time: "12:38", text: t("marketing.mock.multi.line1") },
      { speaker: "Dana", time: "12:40", text: t("marketing.mock.multi.line2") },
    ],
    notes: [
      {
        id: "s1",
        kind: "decision",
        topic: t("marketing.mock.multi.session"),
        text: t("marketing.mock.collab.shared_note1"),
        active: true,
      },
      {
        id: "s2",
        kind: "context",
        topic: t("marketing.mock.multi.session"),
        text: t("marketing.mock.collab.shared_note2"),
        active: true,
      },
    ],
  },
});

export const followThroughDemo = (t: Translate): FollowThroughData => ({
  project: "Hooli launch",
  session: t("marketing.mock.follow.session"),
  started: "2026-10-07T10:00",
  duration: "42m 18s",
  call: {
    clock: "41:52",
    said: { speaker: "Raj", time: "41:40", text: t("marketing.mock.follow.call_raj") },
    promise: { speaker: "Dana", local: true, time: "41:52", text: t("marketing.mock.follow.call_dana") },
    drafting: t("marketing.mock.follow.drafting_to", { name: "Raj" }),
    email: { subject: t("marketing.mock.follow.email_subject"), body: t("marketing.mock.follow.email_body") },
    gmail: t("marketing.mock.follow.gmail_draft"),
    send: t("marketing.mock.follow.send_now"),
  },
  selected: [t("marketing.mock.follow.draft1"), t("marketing.mock.follow.draft2"), t("marketing.mock.follow.draft3")],
  formats: [
    t("marketing.mock.follow.prd"),
    t("marketing.mock.follow.brief"),
    t("marketing.mock.follow.plan"),
    t("marketing.mock.follow.canvas"),
  ],
  picked: 1,
  draft: {
    title: t("marketing.mock.follow.draft_title"),
    lines: [t("marketing.mock.follow.draft1"), t("marketing.mock.follow.draft2"), t("marketing.mock.follow.draft3")],
  },
  followUps: [
    {
      provider: "gmail",
      title: t("marketing.mock.follow.email"),
      adding: t("marketing.mock.follow.adding", { tool: "Gmail" }),
      done: t("marketing.mock.follow.draft_ready"),
    },
    {
      provider: "slack",
      title: t("marketing.mock.follow.slack"),
      adding: t("marketing.mock.follow.adding", { tool: "Slack" }),
      done: t("marketing.mock.follow.done_slack"),
    },
    {
      provider: "calendar",
      title: t("marketing.mock.follow.invite"),
      adding: t("marketing.mock.follow.adding", { tool: "Google Calendar" }),
      done: t("marketing.mock.follow.on_calendar"),
    },
  ],
});

// File and label names stay untranslated like any other name, and the typed
// query with them: the Library searches file names, so it has to match some.
const LABELS = {
  pricing: { name: "Pricing", color: "#16a34a", icon: TbCurrencyDollar },
  hooli: { name: "Hooli", color: "#2563eb", icon: TbBuildingSkyscraper },
  launch: { name: "Launch", color: "#db2777", icon: TbRocket },
  finance: { name: "Finance", color: "#d97706", icon: TbChartBar },
} satisfies Record<string, LibraryLabel>;

export const libraryDemo = (t: Translate): LibraryData => ({
  url: "chief.bot/files",
  project: "Hooli launch",
  user: PEOPLE.dana,
  tasks: [
    { title: t("marketing.mock.library.task_recap"), at: "2026-10-08T14:12" },
    { title: t("marketing.mock.library.task_terms"), at: "2026-10-08T11:05" },
  ],
  query: "pricing",
  files: [
    { name: "Hooli pilot brief.md", labels: [LABELS.hooli], at: "2026-10-07T10:44" },
    { name: "Q3 pricing review.md", source: "notion", labels: [LABELS.pricing], at: "2026-10-06T16:20" },
    { name: "Kickoff whiteboard.png", labels: [LABELS.hooli], at: "2026-10-06T10:03" },
    {
      name: "Pricing toggle",
      filename: "WEB-482.md",
      source: "linear",
      labels: [LABELS.launch],
      at: "2026-10-03T12:47",
    },
    { name: "Enterprise pricing terms", source: "gdrive", labels: [LABELS.finance], at: "2026-10-01T17:10" },
    { name: "Onboarding checklist.xlsx", labels: [LABELS.hooli], at: "2026-09-30T09:15" },
    { name: "Competitor pricing.xlsx", labels: [LABELS.pricing], at: "2026-09-26T15:31" },
    {
      name: "Checkout pricing",
      filename: "#1287.md",
      source: "github",
      labels: [LABELS.launch],
      at: "2026-09-24T10:22",
    },
    {
      name: "Launch timeline",
      filename: "Launch timeline.docx",
      source: "gdrive",
      labels: [LABELS.launch],
      at: "2026-09-22T08:58",
    },
    { name: "Pricing v4.pdf", labels: [LABELS.pricing, LABELS.finance], at: "2026-09-19T13:02" },
    { name: "Brand guidelines.pdf", labels: [LABELS.launch], at: "2026-09-12T11:30" },
  ],
  landed: { name: "Pricing review brief.md", labels: [], at: "2026-10-08T14:41" },
  memoryCount: 12,
  memories: [
    { text: t("marketing.mock.library.memory1"), date: "2026-10-07" },
    { text: t("marketing.mock.library.memory2"), date: "2026-09-30" },
    { text: t("marketing.mock.library.memory3"), date: "2026-09-12" },
  ],
  saved: { text: t("marketing.mock.library.memory4"), date: "2026-10-08" },
});

// A Thursday afternoon: the brief already ran at 8:00 for today's first meeting,
// and Run Now produces one for a meeting booked since.
export const actionsDemo = (t: Translate): ActionsData => ({
  url: "chief.bot/actions",
  project: "Hooli launch",
  user: PEOPLE.dana,
  now: "2026-10-08T15:10",
  tasks: [
    { title: t("marketing.mock.actions.recap_globex"), hoursAgo: 2 },
    { title: t("marketing.mock.actions.run_initech"), hoursAgo: 7 },
  ],
  actions: [
    {
      name: t("marketing.mock.exec.action_prep"),
      description: t("marketing.mock.actions.prep_desc"),
      hoursAgo: 120,
      cron: { hour: "8", weekday: "*", monthDay: "*", location: "America/New_York" },
      runs: [
        { title: t("marketing.mock.actions.run_initech"), hoursAgo: 7, credits: 14 },
        { title: t("marketing.mock.actions.run_globex"), hoursAgo: 31, credits: 11 },
        { title: t("marketing.mock.actions.run_board"), hoursAgo: 55, credits: 16 },
      ],
    },
    {
      name: t("marketing.mock.exec.action_recap"),
      description: t("marketing.mock.actions.recap_desc"),
      hoursAgo: 336,
      trigger: "trigger.session.ended",
      slack: "renewals",
      runs: [
        { title: t("marketing.mock.actions.recap_globex"), hoursAgo: 2, credits: 6 },
        { title: t("marketing.mock.actions.recap_initech"), hoursAgo: 26, credits: 5 },
      ],
    },
    {
      name: t("marketing.mock.exec.action_weekly"),
      description: t("marketing.mock.actions.weekly_desc"),
      hoursAgo: 504,
      cron: { hour: "9", weekday: "Monday", monthDay: "*", location: "America/New_York" },
      runs: [{ title: t("marketing.mock.actions.run_weekly"), hoursAgo: 78, credits: 18 }],
    },
    {
      name: t("marketing.mock.actions.files_name"),
      description: t("marketing.mock.actions.files_desc"),
      hoursAgo: 672,
      trigger: "trigger.new",
      paused: true,
      runs: [{ title: t("marketing.mock.actions.run_files"), hoursAgo: 200, credits: 4 }],
    },
  ],
  prompt: t("marketing.mock.actions.instruction"),
  recipients: ["dana@hooli.com", "raj@hooli.com"],
  ran: { title: t("marketing.mock.exec.brief"), hoursAgo: 0, credits: 12 },
});

export const roundTripDemo = (t: Translate): RoundTripData => ({
  page: t("marketing.mock.exec.page"),
  before: {
    time: "09:55",
    title: t("marketing.mock.exec.brief"),
    lines: [
      t("marketing.mock.roundtrip.brief1"),
      t("marketing.mock.roundtrip.brief2"),
      t("marketing.mock.roundtrip.brief3"),
    ],
  },
  after: {
    time: "10:42",
    heading: t("marketing.mock.roundtrip.summary"),
    lines: [
      t("marketing.mock.roundtrip.summary1"),
      t("marketing.mock.roundtrip.summary2"),
      t("marketing.mock.roundtrip.summary3"),
    ],
  },
});
